import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MagnifyingGlassIcon, XMarkIcon, ChevronDownIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { useOpportunityAnalytics } from '../../../../hooks/useOpportunityAnalytics';
import useDynamicOpportunityFilters from '../../../../hooks/useDynamicOpportunityFilters';

/**
 * OpportunityFilters component - Provides search and filter functionality for startup opportunities
 * Features real-time search with debouncing, company stage filtering, role area filtering, and experience level filtering
 */
const OpportunityFilters = ({ 
  searchTerm = '', 
  onSearchChange,
  filters = {},
  onFilterChange,
  opportunities = [], // Array of opportunities for dynamic filtering
  resultCount = 0, // Number of results for analytics
  className = '' 
}) => {
  const { trackSearch, trackFilter } = useOpportunityAnalytics();
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const [selectedStages, setSelectedStages] = useState(filters.companyStages || []);
  const [selectedRoleAreas, setSelectedRoleAreas] = useState(filters.roleAreas || []);
  const [selectedExperienceLevels, setSelectedExperienceLevels] = useState(filters.experienceLevels || []);
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);

  // Get dynamic filter options from opportunities data
  const { 
    companyStages: stageOptions, 
    roleAreas: roleAreaOptions, 
    experienceLevels: experienceLevelOptions,
    loading: filtersLoading,
    error: filtersError
  } = useDynamicOpportunityFilters(opportunities);

  // Debounce search input to improve performance
  const debounceDelay = 300; // 300ms delay

  // Debounced search function with analytics tracking
  const debouncedSearch = useCallback(
    debounce(async (term) => {
      if (onSearchChange) {
        onSearchChange(term);
      }
      
      // Track search analytics
      if (term.trim()) {
        try {
          await trackSearch(term, {
            companyStages: selectedStages,
            roleAreas: selectedRoleAreas,
            experienceLevels: selectedExperienceLevels
          }, resultCount, {
            source: 'filter_component',
            searchLength: term.length
          });
        } catch (error) {
          console.warn('Failed to track search:', error);
        }
      }
    }, debounceDelay),
    [onSearchChange, trackSearch, selectedStages, selectedRoleAreas, selectedExperienceLevels, resultCount]
  );

  // Update local search term and trigger debounced search
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setLocalSearchTerm(value);
    debouncedSearch(value);
  };

  // Clear search functionality
  const handleClearSearch = () => {
    setLocalSearchTerm('');
    if (onSearchChange) {
      onSearchChange('');
    }
  };

  // Handle company stage filter changes
  const handleStageToggle = (stageValue) => {
    const newSelectedStages = selectedStages.includes(stageValue)
      ? selectedStages.filter(stage => stage !== stageValue)
      : [...selectedStages, stageValue];
    
    setSelectedStages(newSelectedStages);
    updateFilters({ companyStages: newSelectedStages });
  };

  // Handle role area filter changes
  const handleRoleAreaToggle = (roleAreaValue) => {
    const newSelectedRoleAreas = selectedRoleAreas.includes(roleAreaValue)
      ? selectedRoleAreas.filter(area => area !== roleAreaValue)
      : [...selectedRoleAreas, roleAreaValue];
    
    setSelectedRoleAreas(newSelectedRoleAreas);
    updateFilters({ roleAreas: newSelectedRoleAreas });
  };

  // Handle experience level filter changes
  const handleExperienceLevelToggle = (experienceValue) => {
    const newSelectedExperienceLevels = selectedExperienceLevels.includes(experienceValue)
      ? selectedExperienceLevels.filter(level => level !== experienceValue)
      : [...selectedExperienceLevels, experienceValue];
    
    setSelectedExperienceLevels(newSelectedExperienceLevels);
    updateFilters({ experienceLevels: newSelectedExperienceLevels });
  };

  // Helper function to update filters with analytics tracking
  const updateFilters = async (newFilters) => {
    const updatedFilters = {
      ...filters,
      ...newFilters
    };
    
    if (onFilterChange) {
      onFilterChange(updatedFilters);
    }
    
    // Track filter usage analytics
    try {
      await trackFilter(updatedFilters, resultCount, {
        source: 'filter_component',
        filterAction: 'update',
        changedFilters: Object.keys(newFilters)
      });
    } catch (error) {
      console.warn('Failed to track filter usage:', error);
    }
  };

  // Clear all filters with analytics tracking
  const handleClearFilters = async () => {
    setSelectedStages([]);
    setSelectedRoleAreas([]);
    setSelectedExperienceLevels([]);
    
    const clearedFilters = {
      ...filters,
      companyStages: [],
      roleAreas: [],
      experienceLevels: []
    };
    
    if (onFilterChange) {
      onFilterChange(clearedFilters);
    }
    
    // Track filter clear analytics
    try {
      await trackFilter(clearedFilters, resultCount, {
        source: 'filter_component',
        filterAction: 'clear_all',
        previousFilterCount: selectedStages.length + selectedRoleAreas.length + selectedExperienceLevels.length
      });
    } catch (error) {
      console.warn('Failed to track filter clear:', error);
    }
  };

  // Toggle filter dropdown
  const toggleFilterDropdown = () => {
    setIsFilterDropdownOpen(!isFilterDropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isFilterDropdownOpen && !event.target.closest('.filter-dropdown')) {
        setIsFilterDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFilterDropdownOpen]);

  // Sync with external props changes
  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    setSelectedStages(filters.companyStages || []);
    setSelectedRoleAreas(filters.roleAreas || []);
    setSelectedExperienceLevels(filters.experienceLevels || []);
  }, [filters.companyStages, filters.roleAreas, filters.experienceLevels]);

  // Calculate total active filters
  const totalActiveFilters = selectedStages.length + selectedRoleAreas.length + selectedExperienceLevels.length;

  return (
    <div className={`w-full max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>
      <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
        {/* Search Input */}
        <div className="relative flex-1 w-full">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 transition-colors duration-200" aria-hidden="true" />
          </div>
          
          <input
            type="text"
            value={localSearchTerm}
            onChange={handleSearchChange}
            placeholder="Search by company name, description, or keywords..."
            className="block w-full pl-10 pr-10 py-3 border border-gray-300 dark:border-gray-600 rounded-lg 
                     bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 
                     placeholder-gray-500 dark:placeholder-gray-400
                     focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent
                     hover:border-gray-400 dark:hover:border-gray-500
                     shadow-sm hover:shadow-md focus:shadow-md
                     transition-all duration-200 ease-out"
            aria-label="Search startup opportunities"
          />
          
          {/* Clear Search Button */}
          {localSearchTerm && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              <button
                type="button"
                onClick={handleClearSearch}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 
                         hover:bg-gray-100 dark:hover:bg-gray-700
                         transition-all duration-200 ease-out focus:outline-none focus:ring-2 
                         focus:ring-purple-500 rounded-full p-1.5"
                aria-label="Clear search"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        {/* Filter Dropdown */}
        <div className="relative filter-dropdown w-full sm:w-auto">
          <button
            type="button"
            onClick={toggleFilterDropdown}
            className={`inline-flex items-center justify-center w-full sm:w-auto px-4 py-3 border border-gray-300 dark:border-gray-600 
                       rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 
                       hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500
                       focus:outline-none focus:ring-2 focus:ring-purple-500 
                       shadow-sm hover:shadow-md focus:shadow-md
                       transition-all duration-200 ease-out ${
                         totalActiveFilters > 0 ? 'ring-2 ring-purple-500 bg-purple-50 dark:bg-purple-900/20 border-purple-300 dark:border-purple-600' : ''
                       }`}
            aria-label="Filter startup opportunities"
            aria-expanded={isFilterDropdownOpen}
            disabled={filtersLoading}
          >
            <FunnelIcon className="h-5 w-5 mr-2 transition-colors duration-200" aria-hidden="true" />
            <span className="text-sm font-medium whitespace-nowrap">
              {filtersLoading 
                ? 'Loading...'
                : totalActiveFilters > 0 
                  ? `Filters (${totalActiveFilters})` 
                  : 'Filter'
              }
            </span>
            <ChevronDownIcon 
              className={`h-4 w-4 ml-2 transition-transform duration-200 ease-out ${
                isFilterDropdownOpen ? 'rotate-180' : ''
              }`} 
              aria-hidden="true" 
            />
          </button>

          {/* Dropdown Menu */}
          {isFilterDropdownOpen && (
            <div className="absolute right-0 sm:right-0 left-0 sm:left-auto mt-2 w-full sm:w-80 
                           bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 
                           rounded-lg shadow-xl dark:shadow-2xl z-20
                           backdrop-blur-sm bg-white/95 dark:bg-gray-800/95 filter-dropdown-scroll">
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Filter
                  </h3>
                  {totalActiveFilters > 0 && (
                    <button
                      type="button"
                      onClick={handleClearFilters}
                      className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-800 
                               dark:hover:text-purple-300 font-medium
                               hover:bg-purple-50 dark:hover:bg-purple-900/30 px-2 py-1 rounded
                               transition-all duration-200 ease-out"
                    >
                      Clear All
                    </button>
                  )}
                </div>
                
                {/* Company Stage Filters */}
                {stageOptions.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">
                      Company Stage
                    </h4>
                    <div className="space-y-1">
                      {stageOptions.map((option) => (
                      <label
                        key={option.value}
                        className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 
                                 rounded-md p-2 transition-all duration-200 ease-out group"
                      >
                        <input
                          type="checkbox"
                          checked={selectedStages.includes(option.value)}
                          onChange={() => handleStageToggle(option.value)}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 
                                   dark:border-gray-600 rounded transition-colors duration-200"
                        />
                        <div className="ml-3 flex items-center">
                          <div className={`w-2.5 h-2.5 rounded-full mr-2.5 transition-all duration-200 group-hover:scale-110 ${
                            option.color === 'blue' ? 'bg-blue-500 group-hover:bg-blue-400' :
                            option.color === 'green' ? 'bg-green-500 group-hover:bg-green-400' :
                            option.color === 'purple' ? 'bg-purple-500 group-hover:bg-purple-400' :
                            option.color === 'indigo' ? 'bg-indigo-500 group-hover:bg-indigo-400' :
                            option.color === 'pink' ? 'bg-pink-500 group-hover:bg-pink-400' :
                            'bg-orange-500 group-hover:bg-orange-400'
                          }`} />
                          <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors duration-200">
                            {option.label}
                          </span>
                        </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Role Area Filters */}
                {roleAreaOptions.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">
                      Role Area
                    </h4>
                    <div className="space-y-1">
                      {roleAreaOptions.map((option) => (
                      <label
                        key={option.value}
                        className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 
                                 rounded-md p-2 transition-all duration-200 ease-out group"
                      >
                        <input
                          type="checkbox"
                          checked={selectedRoleAreas.includes(option.value)}
                          onChange={() => handleRoleAreaToggle(option.value)}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 
                                   dark:border-gray-600 rounded transition-colors duration-200"
                        />
                        <div className="ml-3 flex items-center">
                          <div className={`w-2.5 h-2.5 rounded-full mr-2.5 transition-all duration-200 group-hover:scale-110 ${
                            option.color === 'green' ? 'bg-green-500 group-hover:bg-green-400' :
                            option.color === 'blue' ? 'bg-blue-500 group-hover:bg-blue-400' :
                            option.color === 'purple' ? 'bg-purple-500 group-hover:bg-purple-400' :
                            option.color === 'pink' ? 'bg-pink-500 group-hover:bg-pink-400' :
                            option.color === 'indigo' ? 'bg-indigo-500 group-hover:bg-indigo-400' :
                            option.color === 'yellow' ? 'bg-yellow-500 group-hover:bg-yellow-400' :
                            option.color === 'teal' ? 'bg-teal-500 group-hover:bg-teal-400' :
                            'bg-orange-500 group-hover:bg-orange-400'
                          }`} />
                          <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors duration-200">
                            {option.label}
                          </span>
                        </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Experience Level Filters */}
                {experienceLevelOptions.length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">
                      Experience Level
                    </h4>
                    <div className="space-y-1">
                      {experienceLevelOptions.map((option) => (
                      <label
                        key={option.value}
                        className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 
                                 rounded-md p-2 transition-all duration-200 ease-out group"
                      >
                        <input
                          type="checkbox"
                          checked={selectedExperienceLevels.includes(option.value)}
                          onChange={() => handleExperienceLevelToggle(option.value)}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 
                                   dark:border-gray-600 rounded transition-colors duration-200"
                        />
                        <div className="ml-3 flex items-center">
                          <div className={`w-2.5 h-2.5 rounded-full mr-2.5 transition-all duration-200 group-hover:scale-110 ${
                            option.color === 'green' ? 'bg-green-500 group-hover:bg-green-400' :
                            option.color === 'blue' ? 'bg-blue-500 group-hover:bg-blue-400' :
                            option.color === 'purple' ? 'bg-purple-500 group-hover:bg-purple-400' :
                            'bg-indigo-500 group-hover:bg-indigo-400'
                          }`} />
                          <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors duration-200">
                            {option.label}
                          </span>
                        </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Show message when no filters are available */}
                {stageOptions.length === 0 && roleAreaOptions.length === 0 && experienceLevelOptions.length === 0 && !filtersLoading && (
                  <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-4">
                    {filtersError ? `Error loading filters: ${filtersError}` : 'No filter options available'}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Active Filters Display */}
      {(localSearchTerm || totalActiveFilters > 0) && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {localSearchTerm && (
            <div className="text-sm text-gray-500 dark:text-gray-400 
                          bg-gray-50 dark:bg-gray-800 px-3 py-1.5 rounded-full border border-gray-200 dark:border-gray-700">
              Searching for "<span className="font-medium text-gray-700 dark:text-gray-300">{localSearchTerm}</span>"
            </div>
          )}
          
          {/* Company Stage Tags */}
          {selectedStages.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedStages.map((stage) => {
                const option = stageOptions.find(opt => opt.value === stage);
                return (
                  <span
                    key={stage}
                    className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium 
                             bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300
                             border border-purple-200 dark:border-purple-700
                             hover:bg-purple-200 dark:hover:bg-purple-900/50
                             transition-all duration-200 ease-out"
                  >
                    <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                      option?.color === 'blue' ? 'bg-blue-500' :
                      option?.color === 'green' ? 'bg-green-500' :
                      option?.color === 'purple' ? 'bg-purple-500' :
                      option?.color === 'indigo' ? 'bg-indigo-500' :
                      option?.color === 'pink' ? 'bg-pink-500' :
                      'bg-orange-500'
                    }`} />
                    {option?.label}
                    <button
                      type="button"
                      onClick={() => handleStageToggle(stage)}
                      className="ml-1.5 hover:text-purple-600 dark:hover:text-purple-400
                               hover:bg-purple-200 dark:hover:bg-purple-800 rounded-full p-0.5
                               transition-all duration-200 ease-out"
                      aria-label={`Remove ${option?.label} filter`}
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}

          {/* Role Area Tags */}
          {selectedRoleAreas.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedRoleAreas.map((roleArea) => {
                const option = roleAreaOptions.find(opt => opt.value === roleArea);
                return (
                  <span
                    key={roleArea}
                    className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium 
                             bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300
                             border border-blue-200 dark:border-blue-700
                             hover:bg-blue-200 dark:hover:bg-blue-900/50
                             transition-all duration-200 ease-out"
                  >
                    <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                      option?.color === 'green' ? 'bg-green-500' :
                      option?.color === 'blue' ? 'bg-blue-500' :
                      option?.color === 'purple' ? 'bg-purple-500' :
                      option?.color === 'pink' ? 'bg-pink-500' :
                      option?.color === 'indigo' ? 'bg-indigo-500' :
                      option?.color === 'yellow' ? 'bg-yellow-500' :
                      option?.color === 'teal' ? 'bg-teal-500' :
                      'bg-orange-500'
                    }`} />
                    {option?.label}
                    <button
                      type="button"
                      onClick={() => handleRoleAreaToggle(roleArea)}
                      className="ml-1.5 hover:text-blue-600 dark:hover:text-blue-400
                               hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5
                               transition-all duration-200 ease-out"
                      aria-label={`Remove ${option?.label} filter`}
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}

          {/* Experience Level Tags */}
          {selectedExperienceLevels.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedExperienceLevels.map((experienceLevel) => {
                const option = experienceLevelOptions.find(opt => opt.value === experienceLevel);
                return (
                  <span
                    key={experienceLevel}
                    className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium 
                             bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300
                             border border-green-200 dark:border-green-700
                             hover:bg-green-200 dark:hover:bg-green-900/50
                             transition-all duration-200 ease-out"
                  >
                    <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                      option?.color === 'green' ? 'bg-green-500' :
                      option?.color === 'blue' ? 'bg-blue-500' :
                      option?.color === 'purple' ? 'bg-purple-500' :
                      'bg-indigo-500'
                    }`} />
                    {option?.label}
                    <button
                      type="button"
                      onClick={() => handleExperienceLevelToggle(experienceLevel)}
                      className="ml-1.5 hover:text-green-600 dark:hover:text-green-400
                               hover:bg-green-200 dark:hover:bg-green-800 rounded-full p-0.5
                               transition-all duration-200 ease-out"
                      aria-label={`Remove ${option?.label} filter`}
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Debounce utility function
 * Delays function execution until after specified delay has passed since last invocation
 */
function debounce(func, delay) {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), delay);
  };
}

export default OpportunityFilters;